import _ from 'underscore';
import { useContext, useEffect, useState, useMemo } from 'react';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { InstrumentationContext } from '@bingads-webui-react/hoc-with-instrumentation';
import { AlertHelper } from '@bingads-webui-campaign/alert-helper';
import { CampaignTypes } from '@bingads-webui-campaign-react/constants';
import {
  getBillingStatus,
  getCampaignDiagnostics,
  Areas,
  getAreasForCampaignType,
} from '@bingads-webui-campaign-react/pmax-overview-data';

import { getBillingDetail } from './status-details-helpers/get-billing-detail';
import { getDiagnosticsDetail } from './status-details-helpers/get-diagnostics-detail';
import { getCardStatusText } from './card-status-text-helper';

export function useData({
  serverContext,
  i18n,
  perfMarker,
  sesId,
  campaignType,
}) {
  const [billing, setBilling] = useState(null);
  const [diagnosticsData, setDiagnosticsData] = useState(null);
  const [isDataFetchDone, setIsDataFetchDone] = useState(false);
  const [error, setError] = useState(null);

  const instrumentation = useContext(InstrumentationContext);
  const { activity } = useMemo(() => instrumentation.createScenarioAndActivity({
    scenarioName: 'Fluent2OverviewPage',
    activityName: 'DiagnosticCard',
  }), [instrumentation]);
  const alertHelper = useMemo(() => new AlertHelper({ i18n }), [i18n]);

  const areas = useMemo(() => getAreasForCampaignType({ activity, campaignType }), [activity, campaignType]);

  const { scope } = useContext(ScopeContext);
  const {
    values: {
      cid: scopeCID,
      aid,
      uid,
      campaignId,
    },
  } = scope;
  const cid = _.get(serverContext, ['CurrentAccount', 'AdvertiserCustomerID']) || scopeCID;

  useEffect(() => {
    if (areas.includes(Areas.Billing)) {
      getBillingStatus({
        cid: scopeCID, aid, uid, ccBaseUrl: _.get(serverContext, ['Urls', 'ClientCenterBaseUrl']), set: setBilling, dfmCacheKey: 'Diagnostic-BillingStatus', setError,
      });
    }

    // We don't want to overwrite the Audience campaigns pilot
    const isNewConversionsEnabled = campaignType !== CampaignTypes.Audience && (_.get(serverContext, ['DynamicPermissions', 'DiagnosticCMMTApi']) || _.get(serverContext, ['DynamicPermissions', 'ConversionWarningImprovement']));
    // This pilot checks only applies to Audience campaigns
    const isNewApiEnabledForAudienceCampaigns = campaignType === CampaignTypes.Audience && _.get(serverContext, ['DynamicPermissions', 'AudienceUnderspendShowTileUseNewAPI']);
    // New API doesn't support App campaigns yet
    const withNewApi = campaignType !== CampaignTypes.App && (isNewConversionsEnabled || isNewApiEnabledForAudienceCampaigns);
    getCampaignDiagnostics({
      cid,
      aid,
      campaignId,
      timeZoneId: _.get(serverContext, ['CurrentAccount', 'TimeZone']),
      adInsightBaseUrl: _.get(serverContext, ['ServicesEndpoints', 'AdInsightAPI']),
      Token: _.get(serverContext, 'Token'),
      set: setDiagnosticsData,
      setError,
      dfmCacheKey: 'Diagnostic-DiagnosticData',
      areas: _.without(areas, Areas.Billing),
      campaignType,
      withNewApi,
      useNewConversionDiagnostics: isNewConversionsEnabled,
    });
  }, [serverContext, aid, campaignId, cid, uid, sesId, scopeCID, areas, campaignType]);

  const billingDetail = useMemo(() => {
    if (!billing || !Array.isArray(billing)) {
      return null;
    }

    return getBillingDetail({
      billing,
      i18n,
      scope,
      ccBaseUrl: _.get(serverContext, ['Urls', 'ClientCenterBaseUrl']),
      activity,
    });
  }, [serverContext, activity, billing, i18n, scope]);

  const diagnosticsDetail = useMemo(() => {
    if (!diagnosticsData || !Array.isArray(_.get(diagnosticsData, 'CampaignDiagnosticDetail'))) {
      return {};
    }
    return getDiagnosticsDetail({
      diagnosticsData, i18n, scope, activity, serverContext, alertHelper, areas, campaignType,
    });
  }, [diagnosticsData, i18n, scope, activity, serverContext, alertHelper, areas, campaignType]);

  const rawDetails = useMemo(() => _.reduce(
    areas,
    (acc, a) => {
      if (a === Areas.Billing) {
        acc.push(billingDetail);
      } else if (diagnosticsDetail[a] !== undefined) {
        acc.push(diagnosticsDetail[a]);
      }
      return acc;
    },
    []
  ), [billingDetail, areas, diagnosticsDetail]);
  const totalStatusCount = rawDetails.length;
  const details = useMemo(
    () => rawDetails.filter(Boolean),
    [rawDetails]
  );

  useEffect(() => {
    if (!isDataFetchDone && details.length === totalStatusCount) {
      setIsDataFetchDone(true);
      perfMarker.done();
    }
  }, [details, isDataFetchDone, perfMarker, totalStatusCount]);

  useEffect(() => {
    if (error) {
      if (details.length > 0) {
        // If we have some details to show, we can dismiss the shimmer
        setIsDataFetchDone(true);
      }

      // readyState 0 means we didn't even send out the request (browser lose focus, unload, etc)
      if (error.readyState && error.readyState !== 0) {
        activity.error(`Diagnostic data fetch error: ${JSON.stringify(error)}`);
        alertHelper.showError(i18n.getString(_TL_('We’ve encountered an error and can’t continue. Please contact support for further assistance.')));
      }
    }
  }, [activity, alertHelper, details.length, error, i18n]);

  const {
    campaignStatusTitle,
    campaignStatusMsg,
  } = getCardStatusText(details, activity, i18n);

  return {
    isDataFetchDone,
    campaignStatusTitle,
    campaignStatusMsg,
    details,
    totalStatusCount,
  };
}
